import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import { Users2, MessageCircle } from "lucide-react";
import ClientShell from "./ClientShell";
import FeatureOnboarding from "./onboarding/FeatureOnboarding";
import { useMessageThreads } from "@/lib/messagingQueries";
import { useFileUploadContext } from "@/lib/fileQueries";
import { ThreadView } from "./messages/ThreadView";

/**
 * Master v5 Prompt 13 — Client Messages. Individual Workspace: exactly
 * one thread. Team Workspace: the team+coach thread plus one private
 * coach-member thread per member — never a member-to-member thread
 * (enforced server-side, `assert_can_send_in_thread`). "Every Team send
 * runs fresh AudienceGate": `audienceSelection` is reset to null after
 * EVERY send, never pre-filled from a previous choice.
 */
export default function MessagesPage() {
  const { workspaceId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { data: threadsData, isPending } = useMessageThreads(workspaceId);
  const { data: uploadContext } = useFileUploadContext(workspaceId);
  const isTeamWorkspace = uploadContext?.access_type === "team";
  const actorRole = uploadContext?.workspace_role === "coach" ? "coach" : "client_member";

  const threads = useMemo(() => threadsData?.items || [], [threadsData]);
  // Deep links (e.g. a Coach Today task) must resolve from the URL alone
  // — router navigation `state` never survives a hard reload/new tab.
  const [activeThreadId, setActiveThreadId] = useState(location.state?.activeThreadId || searchParams.get("thread") || null);

  useEffect(() => {
    if (!activeThreadId && threads.length > 0) setActiveThreadId(threads[0].thread_id);
  }, [threads, activeThreadId]);

  return (
    <ClientShell>
      <div data-testid="messages-page" className="max-w-5xl">
        <h1 className="font-serif text-2xl text-ink mb-1">Messages</h1>
        <p className="text-sm text-ink-soft mb-4">
          {isTeamWorkspace ? "Your team thread and your private thread with your coach." : "A private thread with your coach."}
        </p>
        <FeatureOnboarding
          workspaceId={workspaceId}
          featureKey="messages"
          title="Welcome to Messages"
          body={isTeamWorkspace
            ? "Every message you send asks who should see it — your team & coach, or just your coach — every single time. There's no member-to-member chat."
            : "Send a message directly to your coach. Attach files or link a Session, Assignment or Feedback for context."}
        />
        {isPending ? (
          <p className="text-sm text-ink-soft" data-testid="messages-loading">Loading your threads…</p>
        ) : threads.length === 0 ? (
          <p className="text-sm text-ink-soft" data-testid="messages-empty">No threads yet.</p>
        ) : (
          <div className="grid md:grid-cols-[220px_1fr] gap-4 border border-ink/10 rounded-lg overflow-hidden bg-paper" data-testid="messages-layout">
            <ThreadList threads={threads} activeThreadId={activeThreadId} onSelect={setActiveThreadId} />
            {activeThreadId && (
              <ThreadView
                workspaceId={workspaceId} threadId={activeThreadId} isTeamWorkspace={isTeamWorkspace}
                actorRole={actorRole} teamMembers={uploadContext?.team_members || []}
                onSentToThread={setActiveThreadId}
              />
            )}
          </div>
        )}
      </div>
    </ClientShell>
  );
}

function ThreadList({ threads, activeThreadId, onSelect }) {
  return (
    <div className="border-r border-ink/10 bg-ink/[0.02]" data-testid="messages-thread-list">
      {threads.map((t) => (
        <button
          key={t.thread_id}
          onClick={() => onSelect(t.thread_id)}
          data-testid={`messages-thread-item-${t.thread_id}`}
          className={`w-full text-left px-4 py-3 border-b border-ink/5 transition-colors ${activeThreadId === t.thread_id ? "bg-terracotta/10" : "hover:bg-ink/5"}`}
        >
          <div className="flex items-center gap-2">
            {t.visibility_scope === "workspace_shared" ? <Users2 size={14} className="text-terracotta shrink-0" /> : <MessageCircle size={14} className="text-ink-soft shrink-0" />}
            <span className="text-sm font-medium text-ink truncate flex-1">{t.label}</span>
            {t.unread_count > 0 && (
              <span className="bg-terracotta text-paper text-[10px] font-semibold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1" data-testid={`messages-thread-unread-${t.thread_id}`}>
                {t.unread_count}
              </span>
            )}
          </div>
          {t.last_message && (
            <p className="text-xs text-ink-soft truncate mt-1">{t.last_message.body_text}</p>
          )}
        </button>
      ))}
    </div>
  );
}
